import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Send, FileSearch, BadgeCheck, Eye, Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { maskId } from "@/lib/mask";
import { api, openFileById } from "@/lib/api";
import OfficerShell, { STATUS, ROLE_LABEL } from "./OfficerShell";

// which actions each role may perform
const ROLE_ACTIONS = {
  tehsildar: ["verify", "forward", "approve", "reject", "issue"],
  sdo: ["verify", "forward", "approve", "reject", "issue"],
  collector: ["verify", "approve", "reject", "issue"],
  rdc: ["verify", "approve", "reject", "issue"],
};
const ACTION_META = {
  verify: { label: "पडताळणीत घ्या", Icon: FileSearch, cls: "bg-amber-500 hover:bg-amber-600" },
  forward: { label: "वरिष्ठांकडे पाठवा", Icon: Send, cls: "bg-purple-600 hover:bg-purple-700" },
  approve: { label: "मंजूर करा", Icon: CheckCircle2, cls: "bg-[#5a9e3a] hover:bg-[#4f8b32]" },
  reject: { label: "नामंजूर करा", Icon: XCircle, cls: "bg-red-600 hover:bg-red-700" },
  issue: { label: "प्रमाणपत्र निर्गमित करा", Icon: BadgeCheck, cls: "bg-[#1b5e3f] hover:bg-[#144d33]" },
};

function FileLink({ file }) {
  const open = () => openFileById(file.fileId, file.name);
  if (!file || !file.fileId) return <span className="text-slate-400">—</span>;
  return <button onClick={open} className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#1b5e3f] hover:underline"><Eye className="h-3.5 w-3.5" /> {file.name || "पहा"}</button>;
}

function Row({ label, value }) {
  const isFile = value && typeof value === "object" && value.fileId;
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1.5">
      <span className="text-[13px] text-slate-500">{label}</span>
      <span className="text-right text-[13.5px] font-medium text-slate-800">{isFile ? <FileLink file={value} /> : (value || <span className="text-slate-300">—</span>)}</span>
    </div>
  );
}

const PRELIM_Q = [
  "जोडलेल्या कार्यपद्धतीनुसार अनिवार्य अटींनुसार किमान उच्च माध्यमिक शालांत परीक्षा (HSC) उत्तीर्ण आहे काय?",
  "अर्जदार यांच्या वयाची किमान वय १८ वर्ष पूर्ण आहे काय?",
  "अर्जदार यांना संगणकाचे मूलभूत ज्ञान आहे काय?",
  "अर्जदार संबंधित ग्रामीण/स्थानिक स्वराज्य संस्थेचा रहिवासी आहे काय?",
  "अर्जदारासाठी स्वतःचे आधारकार्ड व पॅनकार्ड जोडलेले आहे काय?",
  "नैतिक अध:पतन, आर्थिक फसवणूक किंवा लाचलुचपत प्रतिबंधक कायद्यांतर्गत गुन्हा दाखल नसल्याबाबतचे स्वयंघोषणापत्र स्वाक्षरी करून जोडलेले आहे काय?",
];

function PrelimSection({ app, onDone }) {
  const existing = app.prelimVerification;
  const [ans, setAns] = useState(() => {
    const q = existing?.questions || {};
    return { q1: q.q1 || "", q2: q.q2 || "", q3: q.q3 || "", q4: q.q4 || "", q5: q.q5 || "", q6: q.q6 || "" };
  });
  const [decision, setDecision] = useState(existing?.decision || "");
  const [remark, setRemark] = useState(existing?.remark || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const d = app.data || {};

  const submit = async () => {
    if (!decision) { setErr("कृपया पात्र / अपात्र अभिप्राय निवडा."); return; }
    setBusy(true); setErr("");
    try { await api.officerPrelim(app.id, { ...ans, decision, remark }); onDone(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E0A106]/50 bg-white p-5">
      <p className="mb-3 rounded bg-[#E0A106] px-3 py-1.5 text-[14px] font-bold text-[#1b3d2a]">प्राथमिक पडताळणी</p>
      <div className="mb-4 grid gap-x-8 md:grid-cols-2">
        <Row label="अर्जदाराचे नाव" value={d.nameMr} />
        <Row label="अर्ज केलेल्या सेवेचा प्रकार" value={d.orgType || "नवीन आपले सरकार सेवा केंद्र"} />
        <Row label="गाव/नगरपंचायत/नगरपरिषद/महानगरपालिका" value={d.village || d.city} />
      </div>
      <div className="space-y-2">
        {PRELIM_Q.map((q, i) => {
          const key = `q${i + 1}`;
          return (
            <div key={key} className="flex flex-col gap-2 rounded-lg border border-slate-200 p-3 sm:flex-row sm:items-start sm:justify-between">
              <p className="text-[13.5px] text-slate-700">{i + 1}. {q}</p>
              <div className="flex shrink-0 gap-2">
                {["होय", "नाही"].map((o) => (
                  <button key={o} type="button" onClick={() => setAns({ ...ans, [key]: o })}
                    className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${ans[key] === o ? (o === "होय" ? "bg-[#5a9e3a] text-white" : "bg-red-500 text-white") : "bg-slate-100 text-slate-600"}`}>{o}</button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <p className="mb-2 text-[14px] font-semibold text-[#1b5e3f]">तहसीलदार यांचा अभिप्राय</p>
        <div className="mb-3 flex gap-2">
          {["पात्र", "अपात्र"].map((o) => (
            <button key={o} type="button" onClick={() => setDecision(o)}
              className={`rounded-full px-5 py-2 text-[14px] font-bold ${decision === o ? (o === "पात्र" ? "bg-[#5a9e3a] text-white" : "bg-red-600 text-white") : "bg-white text-slate-600 ring-1 ring-slate-300"}`}>{o}</button>
          ))}
        </div>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} placeholder="शेरा / टीप (ऐच्छिक)" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
      </div>
      {err && <p className="mt-2 text-[13px] text-red-600">{err}</p>}
      <button disabled={busy} onClick={submit} className="mt-3 rounded-full bg-[#1b5e3f] px-6 py-2.5 text-[14px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-60">
        {busy ? "जतन होत आहे..." : (existing ? "प्राथमिक पडताळणी अद्ययावत करा" : "प्राथमिक पडताळणी जतन करा")}
      </button>
    </div>
  );
}

export default function OfficerAppDetail() {
  const { id } = useParams();
  const { user } = useApp();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try { setApp(await api.officerDetail(id)); } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doAction = async (action) => {
    if ((action === "reject") && !remark.trim()) { setErr("नामंजूरीसाठी शेरा आवश्यक आहे."); return; }
    setBusy(true); setErr("");
    try { const updated = await api.officerAction(id, { action, remark }); setApp(updated); setRemark(""); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (err && !app) return <OfficerShell><p className="text-red-600">{err}</p></OfficerShell>;
  if (user?.role === "viewer") return <OfficerShell><div className="rounded-2xl border border-[#e6dcc4] bg-white p-10 text-center text-slate-500" data-testid="viewer-no-access">केवळ पाहणे — वैयक्तिक अर्ज पाहण्याची परवानगी नाही.</div></OfficerShell>;
  if (!app) return <OfficerShell><p className="text-slate-500">लोड होत आहे...</p></OfficerShell>;
  const d = app.data || {};
  const st = STATUS[app.status] || STATUS.SUBMITTED;
  const actions = ROLE_ACTIONS[user?.role] || [];

  return (
    <OfficerShell>
      <button onClick={() => navigate("/officer/dashboard")} className="mb-4 flex items-center gap-1.5 text-[14px] font-medium text-[#1b5e3f]"><ArrowLeft className="h-4 w-4" /> यादीकडे परता</button>
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[13px] text-slate-500">अर्ज क्रमांक</p>
                <p className="font-latin text-[22px] font-bold text-[#1b3d2a]">{app.applicationNumber}</p>
              </div>
              <span className={`rounded-full px-3.5 py-1.5 text-[13px] font-bold ${st.cls}`}>{st.label}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-6 sm:grid-cols-3">
              <Row label="तहसील" value={app.tehsil} />
              <Row label="उपविभाग" value={app.subdivision} />
              <Row label="मोबाईल" value={d.mobile} />
            </div>
          </div>

          <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
            <p className="mb-2 rounded bg-[#1b5e3f] px-3 py-1.5 text-[14px] font-bold text-white">अर्जदार माहिती</p>
            <div className="grid gap-x-8 md:grid-cols-2">
              <Row label="नाव (मराठी)" value={d.nameMr} />
              <Row label="नाव (इंग्रजी)" value={d.nameEn} />
              <Row label="गाव/शहर" value={d.city} />
              <Row label="रहिवासाचा पत्ता" value={d.address} />
              <Row label="पिनकोड" value={d.pincode} />
              <Row label="ई-मेल" value={d.email} />
              <Row label="आधार क्रमांक" value={maskId(d.aadhaar)} />
              <Row label="पॅन क्रमांक" value={maskId(d.pan)} />
              <Row label="फोटो" value={d.photo} />
              <Row label="आधारकार्ड अपलोड" value={d.aadhaar_file} />
              <Row label="पॅनकार्ड अपलोड" value={d.pan_file} />
              <Row label="स्वघोषणापत्र" value={d.self_declaration_file} />
              <Row label="रहिवासी पत्त्याचा पुरावा १" value={d.residence_proof_file} />
              <Row label="रहिवासी पत्त्याचा पुरावा २" value={d.residence_proof_file_2} />
              <Row label="शुल्क UTR" value={d.fee_utr} />
              <Row label="शुल्क पुरावा" value={d.fee_proof} />
            </div>
          </div>

          {/* preliminary verification */}
          <PrelimSection app={app} onDone={load} />

          {/* history */}
          <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
            <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">कार्यवाही इतिहास</p>
            <div className="space-y-3">
              {(app.history || []).length === 0 && <p className="text-[13.5px] text-slate-400">अजून कोणतीही कार्यवाही नाही.</p>}
              {(app.history || []).map((h, i) => {
                const hs = STATUS[h.status] || {};
                return (
                  <div key={i} className="flex gap-3">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1b5e3f]/10"><Clock className="h-3.5 w-3.5 text-[#1b5e3f]" /></div>
                    <div className="flex-1 border-b border-slate-100 pb-2">
                      <p className="text-[13.5px]"><strong>{h.by}</strong> <span className="text-slate-400">({ROLE_LABEL[h.role] || h.role})</span> — <span className={`rounded px-1.5 py-0.5 text-[12px] font-semibold ${hs.cls || ""}`}>{hs.label || h.action}</span></p>
                      {h.remark && <p className="text-[13px] text-slate-600">{h.remark}</p>}
                      <p className="text-[11.5px] text-slate-400">{new Date(h.at).toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* action panel */}
        <div className="h-fit rounded-2xl border border-[#e6dcc4] bg-white p-5 lg:sticky lg:top-4">
          <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">कार्यवाही</p>
          <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="शेरा / टीप (optional; नामंजूरीसाठी आवश्यक)" className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
          {err && <p className="mb-2 text-[12.5px] text-red-600">{err}</p>}
          <div className="space-y-2">
            {actions.map((a) => {
              const m = ACTION_META[a];
              return (
                <button key={a} disabled={busy} onClick={() => doAction(a)} className={`flex w-full items-center justify-center gap-1.5 rounded-full py-2.5 text-[13.5px] font-semibold text-white transition-colors disabled:opacity-60 ${m.cls}`}>
                  <m.Icon className="h-4 w-4" /> {m.label}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11.5px] text-slate-400">{user?.role === "tehsildar" ? "आवश्यकतेनुसार अर्ज SDO कडे पाठवा." : user?.role === "sdo" ? "आवश्यकतेनुसार अर्ज जिल्हाधिकारी यांकडे पाठवा." : "जिल्हास्तरीय अंतिम निर्णय."}</p>
        </div>
      </div>
    </OfficerShell>
  );
}
